// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { changeEndpointCustomSettings, getEndpointBehaviorConfig } from '@api/portalServices';
import type { ConfigGetResponse, CustomSettingsChangeRequest } from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const BEHAVIOR_CONFIG = { featureKey: 'eamc', configType: 'advanced-settings' } as const;

/**
 * Backs the endpoint-behavior drawer: loads the advanced-settings config
 * (including the custom-settings category) and exposes a mutation to apply a
 * per-row diff. On a successful change the config query is invalidated so the
 * drawer reflects the saved state on the next open.
 */
export const useEndpointBehavior = (enabled: boolean) => {
  const { tenantId } = useSession();
  const queryClient = useQueryClient();
  const configKey = [QueryKey.ENDPOINT_BEHAVIOR, tenantId];

  const config = useQuery<ConfigGetResponse, ApiError>({
    queryKey: configKey,
    queryFn: () => getEndpointBehaviorConfig(BEHAVIOR_CONFIG),
    enabled: enabled && Boolean(tenantId),
    retry: false,
  });

  const change = useMutation<void, ApiError, CustomSettingsChangeRequest>({
    mutationFn: changeEndpointCustomSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.ENDPOINT_BEHAVIOR] });
    },
    retry: false,
  });

  return { config, change };
};
